/**
 * Wavefront OBJ/MTL loader pipeline.
 *
 * Wraps Three.js OBJLoader + MTLLoader with a Promise-based API
 * and returns a normalized ModelResult.
 */

import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { MTLLoader } from 'three/addons/loaders/MTLLoader.js';
import { Logger } from '../core/Logger.js';
import type { ModelResult, LoadOptions } from './ModelPipeline.js';

/* ------------------------------------------------------------------ */
/*  Pipeline                                                          */
/* ------------------------------------------------------------------ */

export class OBJPipeline {
  private objLoader: OBJLoader;
  private mtlLoader: MTLLoader;

  constructor() {
    this.objLoader = new OBJLoader();
    this.mtlLoader = new MTLLoader();
  }

  /**
   * Load a Wavefront OBJ file with optional MTL material companion.
   *
   * @param url - URL of the .obj file
   * @param options - Optional load options (mtlUrl, texturePath, onProgress)
   */
  async load(url: string, options: LoadOptions = {}): Promise<ModelResult> {
    const { mtlUrl, texturePath, onProgress } = options;

    // Fresh loader per load to avoid material state leaking between calls
    this.objLoader = new OBJLoader();

    // If an MTL URL is provided, load materials first
    if (mtlUrl) {
      const materialCreator = await this.loadMTL(mtlUrl, texturePath);
      materialCreator.preload();
      this.objLoader.setMaterials(materialCreator);
    }

    return this.loadOBJ(url, onProgress);
  }

  /* ---------------------------------------------------------------- */
  /*  Private helpers                                                  */
  /* ---------------------------------------------------------------- */

  private loadMTL(
    url: string,
    texturePath?: string,
  ): Promise<MTLLoader.MaterialCreator> {
    return new Promise((resolve, reject) => {
      if (texturePath) {
        this.mtlLoader.setResourcePath(texturePath);
      }

      this.mtlLoader.load(
        url,
        (materialCreator) => {
          Logger.debug('Assets', `Loaded MTL: ${url}`);
          resolve(materialCreator);
        },
        undefined,
        (error) => {
          Logger.error('Assets', `Failed to load MTL: ${url}`, error);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  }

  private loadOBJ(
    url: string,
    onProgress?: (event: ProgressEvent) => void,
  ): Promise<ModelResult> {
    return new Promise((resolve, reject) => {
      this.objLoader.load(
        url,
        (group) => {
          Logger.debug('Assets', `Loaded OBJ: ${url}`);
          resolve({
            scene: group,
            scenes: [group],
            cameras: [],
            animations: [],
            format: 'obj',
            meta: { url },
          });
        },
        onProgress,
        (error) => {
          Logger.error('Assets', `Failed to load OBJ: ${url}`, error);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  }

  dispose(): void {
    // OBJLoader and MTLLoader have no dispose methods;
    // release references for GC.
    this.objLoader = new OBJLoader();
    this.mtlLoader = new MTLLoader();
  }
}
